import {
  Activity,
  Airplay,
  BookOpen,
  CalendarDays,
  Check,
  Download,
  FileText,
  FlaskConical,
  Lock,
  ShieldAlert,
  ShieldCheck,
  Syringe,
} from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { CardActionButtons } from "../../components/card/CardActionButtons";
import { CardFlip } from "../../components/card/CardFlip";
import { HealthPassCardBack } from "../../components/card/HealthPassCardBack";
import { HealthPassCardFront } from "../../components/card/HealthPassCardFront";
import { SharedConsentsBox } from "../../components/card/SharedConsentsBox";
import { walletItemLabel } from "../../components/card/cardStyle";
import { useHealthPassCard } from "../../hooks/useHealthPassCard";
import { useI18nStore } from "../../stores/i18nStore";
import { exportCardAsPDF, type PaperFormat } from "../../utils/cardPDFExport";

/** Maps wallet item type → monochrome Lucide icon component */
const WALLET_ICONS: Record<string, React.FC<{ className?: string }>> = {
  emergency: ShieldAlert,
  medications: Activity,
  vaccinations: Syringe,
  checkin: Airplay,
  appointments: CalendarDays,
  insurance: ShieldCheck,
  travel_docs: FileText,
  lab_results: FlaskConical,
};

const CardPage: React.FC = () => {
  const { t } = useI18nStore();
  const navigate = useNavigate();
  const { cardData, walletItems, qrData, isLoading, isQrLoading } =
    useHealthPassCard();

  const [isFlipped, setIsFlipped] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  // The offline code carries the emergency summary itself, so a responder can
  // read it with no network at all; the online code opens the live record.
  const [offlineCode, setOfflineCode] = useState(false);

  const handlePDF = async (format: PaperFormat) => {
    setShowPdfMenu(false);
    if (!cardData) return;
    try {
      await exportCardAsPDF(
        cardData,
        walletItems,
        format,
        qrData?.qrDataUrl ?? null,
      );
    } catch {
      toast.error(t.cardDownloadFailed);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-5" aria-busy="true">
        <div className="mx-auto aspect-[85.6/54] w-full max-w-[342px] animate-pulse rounded-[18px] bg-primary-100" />
        <div className="h-[260px] animate-pulse rounded-[22px] bg-card" />
      </div>
    );
  }

  const hasOfflineCode =
    !!qrData?.offlineFhirQrDataUrl &&
    qrData.offlineFhirQrDataUrl !== qrData.qrDataUrl;

  const sideCls = (on: boolean) =>
    `min-h-[40px] flex-1 rounded-full px-4 text-sm font-semibold transition-colors ${
      on ? "bg-card text-ink shadow-sm" : "text-muted-foreground"
    }`;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="font-display text-[28px] font-extrabold leading-tight text-ink sm:text-4xl">
          {t.navCardWallet}
        </h1>
      </header>

      {/* The card (UX_CONCEPT.md §5.2): a Front / Wallet switch, plus tap to
          flip. gap-6: the action tiles read as attached to the card at 16px. */}
      <div className="flex flex-col items-center gap-6">
        <div
          role="group"
          aria-label={t.healthpassCard}
          className="flex w-full max-w-[342px] gap-1 rounded-full bg-muted p-1"
        >
          <button
            type="button"
            aria-pressed={!isFlipped}
            onClick={() => setIsFlipped(false)}
            className={sideCls(!isFlipped)}
          >
            {t.cardSideFront}
          </button>
          <button
            type="button"
            aria-pressed={isFlipped}
            onClick={() => setIsFlipped(true)}
            className={sideCls(isFlipped)}
          >
            {t.cardSideWallet}
          </button>
        </div>

        <CardFlip
          isFlipped={isFlipped}
          onFlip={() => setIsFlipped((f) => !f)}
          front={
            <HealthPassCardFront
              cardData={cardData}
              qrData={
                offlineCode && qrData
                  ? { ...qrData, qrDataUrl: qrData.offlineFhirQrDataUrl }
                  : qrData
              }
              isQrLoading={isQrLoading}
            />
          }
          back={
            <HealthPassCardBack cardData={cardData} walletItems={walletItems} />
          }
        />

        {hasOfflineCode && !isFlipped && (
          <div className="-mt-2 max-w-[342px] text-center">
            <button
              type="button"
              aria-pressed={offlineCode}
              onClick={() => setOfflineCode((v) => !v)}
              className="min-h-[40px] rounded-full px-4 text-sm font-semibold text-primary-700 hover:bg-primary-50"
            >
              {offlineCode ? t.cardOnlineCodeShow : t.cardOfflineCodeShow}
            </button>
            {offlineCode && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t.cardOfflineCodeHint}
              </p>
            )}
          </div>
        )}

        {/* Check-in | Emergency | Med access | Wallet */}
        <CardActionButtons
          onWalletClick={() => navigate("/traveler/card/wallet")}
        />

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPdfMenu((v) => !v)}
            aria-expanded={showPdfMenu}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-input bg-card px-5 text-sm font-semibold text-ink hover:bg-muted"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {t.cardDownload}
          </button>
          {showPdfMenu && (
            <>
              <div className="absolute left-1/2 top-full z-20 mt-2 min-w-[180px] -translate-x-1/2 overflow-hidden rounded-2xl bg-card py-1 shadow-lg ring-1 ring-border">
                {(["a4", "a5", "letter", "card"] as PaperFormat[]).map(
                  (fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => handlePDF(fmt)}
                      className="block min-h-[44px] w-full px-4 text-left text-sm text-ink hover:bg-muted"
                    >
                      {fmt === "a4"
                        ? t.printFormatA4
                        : fmt === "a5"
                          ? t.printFormatA5
                          : fmt === "letter"
                            ? t.printFormatLetter
                            : t.printFormatCard}
                    </button>
                  ),
                )}
              </div>
              <button
                type="button"
                aria-label={t.close}
                className="fixed inset-0 z-10 h-full w-full cursor-default bg-transparent"
                onClick={() => setShowPdfMenu(false)}
              />
            </>
          )}
        </div>
      </div>

      {/* The wallet, slot by slot */}
      <section aria-label={t.navWallet} className="rounded-[22px] bg-card">
        <div className="flex items-baseline justify-between px-5 pt-5">
          <h2 className="font-display text-lg font-bold text-ink">
            {t.navWallet}
          </h2>
          <button
            type="button"
            onClick={() => navigate("/traveler/card/wallet")}
            className="text-sm font-semibold text-primary-700 hover:underline"
          >
            {t.viewAll}
          </button>
        </div>
        <ul className="mt-2 divide-y divide-border">
          {walletItems.map((item) => {
            const Icon = WALLET_ICONS[item.type] ?? BookOpen;
            const locked = item.status === "locked";
            return (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!item.available}
                  onClick={() => {
                    if (item.available)
                      navigate(`/traveler/card/wallet/${item.id}`);
                  }}
                  className={`flex min-h-[60px] w-full items-center gap-3 px-5 py-3 text-left ${
                    item.available ? "hover:bg-muted/60" : "cursor-not-allowed"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl ${
                      locked
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary-50 text-primary-700"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[15px] font-semibold ${locked ? "text-muted-foreground" : "text-ink"}`}
                    >
                      {walletItemLabel(item, t)}
                    </span>
                    {locked && (
                      <span className="block text-xs text-muted-foreground">
                        {t.upgradeForWalletItem.replace(
                          "{tier}",
                          item.requiredTier,
                        )}
                      </span>
                    )}
                  </span>
                  {locked ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                      <Lock className="h-3 w-3" aria-hidden="true" />
                      {item.requiredTier}
                    </span>
                  ) : item.count !== null ? (
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-bold text-primary-700">
                      {item.count}
                    </span>
                  ) : item.status === "complete" ? (
                    <Check
                      className="h-4 w-4 text-health-700"
                      aria-label={t.frDone}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <SharedConsentsBox />
    </div>
  );
};

export default CardPage;
